/**
 * Lovable checkout handoff — see templates/page.lovable-checkout.liquid.
 *
 * Lovable has already put the items (with selling plans) and cart attributes in the cart.
 * This adds the ReCharge bundle properties using the same payload builder as the main-site
 * cart (window.bear.getBundleItems in cart-to-bundle.js), then continues to /checkout.
 *
 * Safety rules:
 *  - Nothing touches the cart until the bundled line items are fully built and validated.
 *  - Once the cart is being rewritten, no timer can redirect; the rewrite finishes, is
 *    verified, and the original cart is restored if anything is off.
 *  - Every outcome ends at /checkout with the original query string (discount, locale).
 *  - The outcome is stored on the cart as the hidden attribute `_lovable_bundle` for auditing.
 */
;(function () {
  var params = new URLSearchParams(window.location.search)
  var DEBUG = params.has('igo_debug')
  var config = window.igoLovableCheckout || {}

  var SDK_READY_TIMEOUT = 5000
  var SDK_CALL_TIMEOUT = 8000
  var STATUS_WRITE_TIMEOUT = 3000

  // Lovable collection names and theme bundle keys -> theme bundle key
  var BUNDLE_KEY_MAP = {
    west: 'default',
    east: 'toronto',
    usa: 'usa-west',
    usawest: 'usa-west',
    usaeast: 'usa-east',
    default: 'default',
    toronto: 'toronto',
    'usa-west': 'usa-west',
    'usa-east': 'usa-east',
  }

  // theme bundle key -> regional collection handle (window.bear.collectionMap)
  var KEY_COLLECTION = {
    default: 'west',
    toronto: 'east',
    'usa-west': 'usa',
    'usa-east': 'usaeast',
  }

  var locale = (params.get('locale') || '').toLowerCase()
  if (locale.indexOf('fr') === 0) {
    var textEl = document.querySelector('[data-igo-lc-text]')
    if (textEl) {
      textEl.textContent = textEl.getAttribute('data-text-fr')
      document.documentElement.setAttribute('lang', 'fr')
    }
  }

  function checkoutUrl() {
    var p = new URLSearchParams(window.location.search)
    p.delete('igo_debug')
    p.delete('igo_key')
    var q = p.toString()
    return '/checkout' + (q ? '?' + q : '')
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms) })
  }

  function withTimeout(promise, ms, label) {
    return Promise.race([
      promise,
      delay(ms).then(function () { throw new Error(label) }),
    ])
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart_read_' + r.status)
        return r.json()
      })
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (r) {
      if (!r.ok) throw new Error(url + '_' + r.status)
      return r.json()
    })
  }

  function isBundled(item) {
    return !!(item.properties && item.properties._rc_bundle)
  }

  function totalQuantity(items) {
    return items.reduce(function (sum, item) { return sum + item.quantity }, 0)
  }

  // Cart line -> /cart/add.js item, preserving selling plan and properties
  function toAddItem(item) {
    var entry = { id: item.variant_id, quantity: item.quantity, properties: item.properties || {} }
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      entry.selling_plan = item.selling_plan_allocation.selling_plan.id
    }
    return entry
  }

  function waitForSdk() {
    var start = Date.now()
    return new Promise(function (resolve, reject) {
      (function check() {
        if (window.recharge && window.bear && typeof window.bear.getBundleItems === 'function') {
          return resolve()
        }
        if (Date.now() - start > SDK_READY_TIMEOUT) return reject(new Error('sdk_not_ready'))
        setTimeout(check, 50)
      })()
    })
  }

  /**
   * Regional collections every regional product in the cart belongs to. Products outside all
   * regional collections (e.g. gifts) are neutral. Error when a product wasn't rendered.
   */
  function sharedRegions(cart) {
    var regionsByProduct = config.productRegions || {}
    var candidates = null
    for (var i = 0; i < cart.items.length; i++) {
      var regions = regionsByProduct[cart.items[i].product_id]
      if (!regions) return { error: 'region_unknown_product' }
      if (!regions.length) continue
      candidates = candidates === null
        ? regions.slice()
        : candidates.filter(function (r) { return regions.indexOf(r) !== -1 })
    }
    if (!candidates || !candidates.length) return { error: 'region_none' }
    return { regions: candidates }
  }

  /**
   * Region comes from the hidden `_igo_bundle_key` cart attribute Lovable sends, and must agree
   * with the products' regional collections — cart attributes survive /cart/clear, so a key
   * left over from an earlier session must not decide the bundle. Without a key, fall back to
   * the products' collections only when exactly one region fits (not the case in Canada, where
   * meals are shared by `west` and `east`).
   */
  function resolveBundleKey(cart) {
    var shared = sharedRegions(cart)
    if (shared.error) return shared

    // debug only: ?igo_debug=1&igo_key=east simulates the attribute Lovable will send
    var attrKey = (DEBUG && params.get('igo_key')) || (cart.attributes && cart.attributes._igo_bundle_key)
    if (attrKey) {
      var key = BUNDLE_KEY_MAP[String(attrKey).toLowerCase()]
      if (!key) return { error: 'unknown_key_' + attrKey }
      if (shared.regions.indexOf(KEY_COLLECTION[key]) === -1) {
        return { error: 'key_region_mismatch_' + attrKey + '_vs_' + shared.regions.join('+') }
      }
      return { key: key, source: 'attribute' }
    }

    if (shared.regions.length > 1) return { error: 'region_ambiguous_' + shared.regions.join('+') }
    return { key: BUNDLE_KEY_MAP[shared.regions[0]], source: 'collection' }
  }

  function bundleConfigured(key) {
    var product = window.bear.bundleProductMap && window.bear.bundleProductMap[key]
    var collection = window.bear.collectionMap && window.bear.collectionMap[key]
    return !!(product && product.variants && product.variants.length && collection)
  }

  // Build and validate bundled line items without touching the cart
  function buildBundledItems(cart) {
    var resolved = resolveBundleKey(cart)
    if (resolved.error) return Promise.resolve({ skip: resolved.error })

    return waitForSdk()
      .then(function () {
        if (!bundleConfigured(resolved.key)) return { skip: 'bundle_not_configured_' + resolved.key }
        return withTimeout(window.bear.getBundleItems(cart, resolved.key), SDK_CALL_TIMEOUT, 'sdk_timeout')
          .then(function (result) {
            var items = result && result.items
            if (!items || items.length !== cart.items.length) return { skip: 'sdk_incomplete' }
            if (!items.every(isBundled)) return { skip: 'sdk_missing_props' }
            return { key: resolved.key, source: resolved.source, items: items }
          })
      })
      .catch(function (err) {
        return { skip: (err && err.message) || 'sdk_error' }
      })
  }

  function clearCart() {
    var attempts = 0
    function attempt() {
      attempts++
      return postJson('/cart/clear.js')
        .then(getCart)
        .then(function (cart) {
          if (cart.item_count === 0) return
          if (attempts >= 3) throw new Error('clear_failed')
          return delay(300).then(attempt)
        })
    }
    return attempt()
  }

  function replaceCart(items) {
    return clearCart().then(function () {
      return postJson('/cart/add.js', { items: items })
    })
  }

  function matches(cart, expectedLines, expectedQty, requireBundled) {
    return cart.items.length === expectedLines &&
      totalQuantity(cart.items) === expectedQty &&
      (!requireBundled || cart.items.every(isBundled))
  }

  function restore(snapshot, lines, qty) {
    function attempt() {
      return replaceCart(snapshot)
        .then(getCart)
        .then(function (cart) { return matches(cart, lines, qty, false) })
        .catch(function () { return false })
    }
    return attempt().then(function (ok) { return ok || attempt() })
  }

  // Rewrite the cart with bundle properties; restore the original cart on any mismatch
  function applyBundle(cart, built) {
    var snapshot = cart.items.map(toAddItem)
    var lines = cart.items.length
    var qty = totalQuantity(cart.items)

    window.__igoLcMutating = true
    return replaceCart(built.items)
      .then(getCart)
      .then(function (after) {
        if (matches(after, lines, qty, true)) return 'ok:' + built.key
        return restore(snapshot, lines, qty).then(function (ok) {
          return ok ? 'restored:verify_failed' : 'failed:restore'
        })
      })
      .catch(function (err) {
        var reason = (err && err.message) || 'rewrite_error'
        return restore(snapshot, lines, qty).then(function (ok) {
          return ok ? 'restored:' + reason : 'failed:restore'
        })
      })
  }

  function writeStatus(status) {
    return withTimeout(
      postJson('/cart/update.js', { attributes: { _lovable_bundle: status } }),
      STATUS_WRITE_TIMEOUT,
      'status_timeout'
    ).catch(function () {})
  }

  function finish(status) {
    window.__igoLcDone = true
    return writeStatus(status).then(function () {
      window.location.replace(checkoutUrl())
    })
  }

  function showDebug(data) {
    var pre = document.createElement('pre')
    pre.className = 'igo-lc__debug'
    pre.textContent = JSON.stringify(data, null, 2)
    document.querySelector('.igo-lc').appendChild(pre)
    var spinner = document.querySelector('.igo-lc__spinner')
    if (spinner) spinner.style.display = 'none'
  }

  function run() {
    return getCart().then(function (cart) {
      if (!cart.items.length) return DEBUG ? showDebug({ status: 'skipped:empty_cart' }) : finish('skipped:empty_cart')
      if (cart.items.every(isBundled)) return DEBUG ? showDebug({ status: 'already_bundled' }) : finish('already_bundled')

      return buildBundledItems(cart).then(function (built) {
        if (DEBUG) {
          return showDebug({
            status: built.skip ? 'skipped:' + built.skip : 'would_bundle',
            bundleKey: built.key,
            keySource: built.source,
            attributes: cart.attributes,
            checkoutUrl: checkoutUrl(),
            lines: cart.items.map(function (item) {
              var b = built.items && built.items.find(function (x) { return +x.id === +item.variant_id })
              return {
                title: item.title,
                qty: item.quantity,
                sellingPlan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : null,
                regions: (config.productRegions || {})[item.product_id],
                bundleProps: b ? b.properties : null,
              }
            }),
          })
        }
        if (built.skip) return finish('skipped:' + built.skip)
        return applyBundle(cart, built).then(finish)
      })
    })
  }

  run().catch(function (err) {
    if (DEBUG) return showDebug({ status: 'error', message: err && err.message })
    // Only reachable before any cart rewrite (applyBundle handles its own errors)
    if (!window.__igoLcMutating) finish('skipped:' + ((err && err.message) || 'error'))
  })
})()
